import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NIL as NIL_UUID } from 'uuid';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context';
import { ChatContext } from '../../../../../../Chat.constants';
import {
  tChatEvent,
  tInteractiveInbound,
} from '../../../../../../Chat.types';
import useBubbleDate from '../../../../bubbleDate/useBubbleDate.hook';
import './InteractiveReplyBubble.styles.scss';

type tInteractiveReplyBubbleProps = {
  event: tChatEvent;
  interactive: tInteractiveInbound;
};

// Renders customer taps on outbound interactive messages as a WhatsApp-Web
// style inbound bubble (left-aligned, gray, tail at top-left) so the reply
// sits visually alongside regular inbound messages instead of standing out
// as a distinct blue-card.
//
// Event-shape contract from WBS PR #622 / messaging PR #820:
//   - reply body nested under `reply.button_reply` or `reply.list_reply`
//   - `content.quotedEventID` points at the original outbound interactive
//     message so we can render the referenced bubble above the reply (same
//     pattern as MessageBubbleV2 for text replies). This mirrors WhatsApp
//     Web, where the button reply always shows a clickable quote of the
//     original message it answered.
export default function InteractiveReplyBubble(
  props: tInteractiveReplyBubbleProps,
) {
  const { t } = useTranslation(['chat/chat']);
  const bubbleDate = useBubbleDate();
  const { event, interactive } = props;
  const { selectedLeadForChatModal } = useModalsContext();
  // Use the context directly to avoid pulling Chat.hook.ts's firebase chain
  // into consumers that only need `messages.events` to resolve the quoted
  // reference.
  const { messages } = useContext(ChatContext);

  const quotedEventID = event.content?.quotedEventID;
  const hasQuotedEvent = Boolean(
    quotedEventID && quotedEventID !== NIL_UUID,
  );

  // Rely on the already-loaded chat stream — the original outbound
  // interactive is almost always in `messages.events` because the reply is
  // shown right after it. If it's scrolled out of range we simply omit the
  // quote; the reply's own body (selected option title) is still shown.
  const quotedMessage = useMemo<tChatEvent | undefined>(() => {
    if (!hasQuotedEvent || !quotedEventID) return undefined;
    return messages?.events?.find((evt) => evt.id === quotedEventID);
  }, [quotedEventID, hasQuotedEvent, messages?.events]);

  const reply = interactive.reply;
  const isFlowReply = reply.kind === 'flow_reply';
  const body =
    reply.kind === 'button_reply'
      ? reply.button_reply
      : reply.kind === 'list_reply'
        ? reply.list_reply
        : undefined;
  const title = body?.title ?? '';
  const description =
    reply.kind === 'list_reply' ? reply.list_reply?.description : undefined;
  // Optional chaining is load-bearing (BUG-728): if an upstream (bff-web) drops
  // the flow_reply body, `reply.flow_reply` arrives undefined at runtime even
  // though the type says otherwise. Reading `.answers` directly threw a
  // TypeError that the chat_panel error boundary escalated into unmounting the
  // whole chat. With `?.` it degrades to the flowReplyEmpty fallback instead.
  const flowAnswers =
    reply.kind === 'flow_reply' ? reply.flow_reply?.answers : undefined;

  // Meta edge case: empty title + empty id arrives occasionally. Fall back to
  // a generic "Customer selected an option" string so the bubble still
  // renders instead of collapsing empty. For flow_reply, "content" means at
  // least one answer — a display-only flow (no user inputs) legitimately
  // completes with zero answers and gets its own fallback copy.
  const hasContent = isFlowReply
    ? Boolean(flowAnswers && flowAnswers.length > 0)
    : Boolean(title || body?.id);

  const quotedTitle = quotedMessage?.message?.fromMe
    ? t('messageBubble.you')
    : (selectedLeadForChatModal?.name ??
      quotedMessage?.message?.participant?.name ??
      '');
  const quotedInteractive = quotedMessage?.content?.interactive;
  const quotedHeaderText =
    quotedInteractive && 'header' in quotedInteractive
      ? quotedInteractive.header?.type === 'text'
        ? quotedInteractive.header.text
        : undefined
      : undefined;
  const quotedBody =
    (quotedInteractive && 'body' in quotedInteractive
      ? quotedInteractive.body
      : undefined) ?? quotedMessage?.content?.text;

  return (
    <div className="MessageBubbleV2 InteractiveReplyBubble">
      <div className="MessageBubbleV2__Wrapper col-span-12 chat-start">
        <div className="group flex items-end gap-1">
          <div className="MessageBubbleV2__Container MessageBubbleV2__Container--tail-start !bg-gray-50 InteractiveReplyBubble__Container">
            <div
              className="MessageBubbleV2__Header justify-between InteractiveReplyBubble__Header"
              role="status"
            >
              <span className="InteractiveReplyBubble__Kind">
                {t(`interactive.replyBubble.kinds.${reply.kind}`)}
              </span>
              <span className="InteractiveReplyBubble__Date">
                {bubbleDate.stamp(event?.createdAt ?? '')}
              </span>
            </div>

            {quotedMessage ? (
              <div className="InteractiveReplyBubble__Quote">
                {quotedTitle && (
                  <h6 className="InteractiveReplyBubble__Quote__Title">
                    {quotedTitle}
                  </h6>
                )}
                {quotedHeaderText && (
                  <p className="InteractiveReplyBubble__Quote__Header">
                    {quotedHeaderText}
                  </p>
                )}
                {quotedBody && (
                  <p className="InteractiveReplyBubble__Quote__Body">
                    {quotedBody}
                  </p>
                )}
              </div>
            ) : null}

            <div className="InteractiveReplyBubble__Body">
              {isFlowReply ? (
                hasContent ? (
                  <ul className="InteractiveReplyBubble__Answers">
                    {flowAnswers!.map((answer, idx) => (
                      // Keys are the flow's technical field name, not
                      // guaranteed unique in theory but are in practice
                      // (Meta emits one answer per input); index as a
                      // last-resort tiebreaker only.
                      //
                      // INT-1321: `label` / `value_label` are the backend's
                      // resolved rendering ("Talla de pañal" / "2. Talla M").
                      // Answers captured before that shipped, and any the
                      // backend could not resolve, carry neither and fall
                      // back to the technical key and the raw option id.
                      <li
                        key={`${answer.key}-${idx}`}
                        className="InteractiveReplyBubble__Answer"
                      >
                        <p className="InteractiveReplyBubble__Answer__Key">
                          {answer.label || answer.key}
                        </p>
                        <p className="InteractiveReplyBubble__Answer__Value">
                          {answer.value_label || answer.value}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="InteractiveReplyBubble__Fallback">
                    {t('interactive.replyBubble.flowReplyEmpty')}
                  </p>
                )
              ) : hasContent ? (
                <>
                  <p className="InteractiveReplyBubble__Title">{title}</p>
                  {description && (
                    <p className="InteractiveReplyBubble__Description">
                      {description}
                    </p>
                  )}
                </>
              ) : (
                <p className="InteractiveReplyBubble__Fallback">
                  {t('interactive.replyBubble.customerSelectedUnknown')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
